import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchOpenPositions } from '../api/apiService';
import { isValidUuid } from './useOpenPositions';

/**
 * Live position sync over the portal's real-time position WebSocket feed,
 * seeded and reconciled over REST via `fetchOpenPositions`.
 *
 * Transport is a PLAIN `ws` WebSocket served by the portal on a dedicated port
 * (8080) — the browser's native `WebSocket`, NOT socket.io. After the socket
 * opens we send a `SUBSCRIBE` per owned trading-account UUID; a single socket
 * may watch several accounts. The server forgets subscriptions on disconnect,
 * so we re-subscribe on every reconnect.
 *
 * Two message types drive local state:
 *   - POSITION_SNAPSHOT  authoritative list of open positions for ONE account,
 *                        sent immediately after each SUBSCRIBE. Replaces that
 *                        account's rows (other accounts are left untouched).
 *   - POSITION_UPDATE    { event: { type, accountId, data, timestamp } } where
 *                        type is POSITION_CREATED | POSITION_UPDATED |
 *                        POSITION_CLOSED. `data` may be a single snapshot OR an
 *                        array (live ticks are coalesced) — normalized before
 *                        use. Treated as a delta and patched by positionId.
 *
 * Field naming: the wire uses camelCase (`positionId`, `symbol`, `entryPrice`,
 * …) while this portal's REST API and positions table use snake_case. Every
 * snapshot — socket or REST — is run through `normalizeSnapshot` so the stored
 * map has ONE consistent, table-ready shape.
 *
 * Recovery: this is a delta channel, so anything emitted while disconnected is
 * lost. On every (re)connect we re-subscribe (fresh POSITION_SNAPSHOT) and run
 * a silent REST reconcile (`fetchOpenPositions`) as a hard-guarantee backstop
 * for a cache that may have gone stale while the socket was down.
 */

// ---------------------------------------------------------------------------
// Configuration (overridable per-env; see .env)
// ---------------------------------------------------------------------------

const env = (key, fallback = '') => process.env[key] ?? fallback;

// Plain `ws` endpoint the portal relays position events on. This is a SEPARATE
// port from the REST API — 8080. Always use wss:// in production (see guide).
const WS_URL = env('REACT_APP_POSITION_WS_URL', 'wss://server.investain.com:8080');
const WS_ENABLED_DEFAULT = env('REACT_APP_POSITION_SYNC_ENABLED', 'true') !== 'false';
// Discard events whose server timestamp is implausibly old (clock skew).
const MAX_EVENT_AGE_MS = 60_000;

// camelCase wire fields (for reference / pass-through guard in normalizeSnapshot)
const CAMEL_FIELDS = new Set([
  'positionId',
  'accountId',
  'symbol',
  'symbolName',
  'entryPrice',
  'exitPrice',
  'currentPrice',
  'unrealizedPl',
  'realizedPnl',
  'totalPnl',
  'stopLoss',
  'takeProfit',
  'marketValue',
  'marginUsed',
  'updatedAt',
  'closedAt',
]);

/**
 * Normalize a position snapshot (wire camelCase OR portal snake_case) into the
 * canonical snake_case shape the dashboard table renders.
 *
 * Only fields that are actually present are written; absent fields are left
 * `undefined` (NOT set to null) so the merge step can restore the previous
 * value for partial `UPDATED` snapshots instead of clobbering it.
 *
 * @param {object} raw
 * @returns {object|null} normalized snapshot, or null if it has no usable id
 */
function normalizeSnapshot(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const positionId = raw.positionId ?? raw.id ?? raw.position_id;
  if (positionId === undefined || positionId === null) return null;

  const accountId = raw.accountId ?? raw.account_id;

  const n = { id: positionId, positionId };
  if (accountId !== undefined) {
    n.account_id = accountId;
    n.accountId = accountId;
  }

  // Pick the first defined value among candidate source keys.
  const pick = (target, ...vals) => {
    for (const v of vals) {
      if (v !== undefined) {
        n[target] = v;
        return;
      }
    }
  };

  pick('symbol_name', raw.symbol_name, raw.symbol, raw.symbolName);
  pick('side', raw.side);
  pick('qty', raw.qty);
  pick('entry_price', raw.entry_price, raw.entryPrice);
  pick('current_price', raw.current_price, raw.currentPrice);
  pick('exit_price', raw.exit_price, raw.exitPrice);
  pick('unrealized_pl', raw.unrealized_pl, raw.unrealizedPl);
  pick('realized_pnl', raw.realized_pnl, raw.realizedPnl);
  pick('total_pnl', raw.total_pnl, raw.totalPnl);
  pick('market_value', raw.market_value, raw.marketValue);
  pick('margin_used', raw.margin_used, raw.marginUsed);
  pick('stop_loss', raw.stop_loss, raw.stopLoss);
  pick('take_profit', raw.take_profit, raw.takeProfit);
  pick('status', raw.status);
  pick('updated_at', raw.updated_at, raw.updatedAt);
  pick('closed_at', raw.closed_at, raw.closedAt);

  // Carry through any extra snake_case fields the REST layer returns that we
  // did not explicitly map (e.g. leverage, opened_at, …).
  for (const [k, v] of Object.entries(raw)) {
    if (v !== undefined && !(k in n) && !CAMEL_FIELDS.has(k)) {
      n[k] = v;
    }
  }

  return n;
}

/**
 * Merge a (possibly partial) snapshot into the previous one. A producer sends
 * only the fields it has in hand, so we never blow away fields we already know.
 * `undefined` values in `next` are treated as "not provided" and the previous
 * value is restored.
 *
 * @param {object|undefined} prev
 * @param {object} next
 * @returns {object}
 */
function mergeSnapshot(prev, next) {
  const merged = { ...(prev || {}), ...next };
  for (const k of Object.keys(next)) {
    if (next[k] === undefined && prev && prev[k] !== undefined) {
      merged[k] = prev[k];
    }
  }
  return merged;
}

/**
 * @typedef {Object} UsePositionSyncResult
 * @property {object[]} positions     Normalized positions (table-ready array).
 * @property {object}   positionsById positionId -> normalized snapshot map.
 * @property {number}   totalCount
 * @property {boolean}  loading       True during the initial REST seed.
 * @property {string|null} error
 * @property {'idle'|'connecting'|'online'|'offline'|'disabled'} status
 * @property {boolean}  connected     status === 'online'.
 * @property {object|null} lastEvent  Last PositionSyncEvent envelope received.
 * @property {() => void} refresh     Manually re-run the REST reconcile.
 */

/**
 * Subscribe to live position updates for one or more trading accounts.
 *
 * @param {object}   options
 * @param {string[]} [options.accountIds] Owned trading account UUIDs.
 * @param {string}   [options.accountId]  Single account (guide-faithful form).
 * @param {string}   [options.accessToken] Portal access token for REST calls.
 * @param {boolean}  [options.enabled]    Seed + connect only when true.
 * @param {boolean}  [options.wsEnabled]  Toggle the WebSocket layer (the REST
 *                                        seed still runs). Defaults to env.
 * @param {number}   [options.reconcileIntervalMs] Optional safety-net REST
 *                                        poll; 0 (default) disables it.
 * @returns {UsePositionSyncResult}
 */
const usePositionSync = ({
  accountIds,
  accountId,
  accessToken,
  enabled = true,
  wsEnabled = WS_ENABLED_DEFAULT,
  reconcileIntervalMs = 0,
} = {}) => {
  const [positionsById, setPositionsById] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('idle');
  const [lastEvent, setLastEvent] = useState(null);

  // De-duplicate + validate the owned account ids. Accepts either the array
  // (dashboard) or a single id (guide-faithful) form.
  const ids = useMemo(() => {
    const set = new Set();
    if (typeof accountId === 'string') set.add(accountId);
    (Array.isArray(accountIds) ? accountIds : []).forEach((id) => set.add(id));
    return [...set].filter(isValidUuid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(accountIds ?? []), accountId]);

  // Stable string key so effects only re-run when the owned set actually
  // changes (financialData can be replaced by WS updates with identical ids).
  const idsKey = ids.join(',');

  // Refs that socket callbacks read so they always see fresh values without
  // forcing socket reconnects on every render.
  const ownedRef = useRef(new Set(ids));
  ownedRef.current = new Set(ids);
  const socketRef = useRef(null); // the single shared WebSocket
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  /**
   * Reconcile a single account over REST: upsert the fetched open positions and
   * drop any local positions for that account that REST no longer reports as
   * open. Scoped to one account so multi-account reconciles don't clobber each
   * other. `silent` avoids touching the top-level `loading` flag (used on the
   * background reconnects, not the initial seed).
   */
  const reconcileAccount = useCallback(
    async (acctId, { silent = false } = {}) => {
      if (!accessToken || !acctId) return;
      try {
        const res = await fetchOpenPositions(accessToken, acctId);
        const rest = res && !res.error ? res.data?.positions : null;
        const normalized = (Array.isArray(rest) ? rest : [])
          .map(normalizeSnapshot)
          .filter(Boolean);
        const restIds = new Set(normalized.map((p) => p.id));

        setPositionsById((prev) => {
          const next = { ...prev };
          // Remove stale local positions for THIS account only.
          for (const [pid, pos] of Object.entries(prev)) {
            if (pos.account_id === acctId && !restIds.has(pid)) {
              delete next[pid];
            }
          }
          // Upsert fresh REST positions.
          for (const pos of normalized) {
            next[pos.id] = mergeSnapshot(prev[pos.id], pos);
          }
          return next;
        });
        if (!silent) setError(null);
      } catch (e) {
        if (!silent) setError('Unable to load positions.');
      }
    },
    [accessToken]
  );

  /**
   * Replace the local rows for ONE account with an authoritative snapshot list
   * (from POSITION_SNAPSHOT or a REST reconcile). Positions for other accounts
   * are left untouched, so a single shared socket watching several accounts
   * stays consistent.
   */
  const applyAccountSnapshot = useCallback((acctId, list) => {
    const incoming = (Array.isArray(list) ? list : [])
      .map(normalizeSnapshot)
      .filter(Boolean);
    setPositionsById((prev) => {
      const next = {};
      // Keep positions belonging to OTHER accounts.
      for (const [pid, pos] of Object.entries(prev)) {
        if (pos.account_id !== acctId) next[pid] = pos;
      }
      // Then this account's authoritative rows (fresh from the snapshot).
      for (const pos of incoming) {
        if (pos.account_id === undefined) pos.account_id = acctId;
        next[pos.id] = pos;
      }
      return next;
    });
  }, []);

  /** Apply a single normalized snapshot as an upsert (CREATED / UPDATED). */
  const applyUpsert = useCallback((snapshot) => {
    setPositionsById((prev) => ({
      ...prev,
      [snapshot.id]: mergeSnapshot(prev[snapshot.id], snapshot),
    }));
  }, []);

  /** Remove a position (CLOSED). */
  const applyRemove = useCallback((snapshot) => {
    setPositionsById((prev) => {
      if (!(snapshot.id in prev)) return prev;
      const next = { ...prev };
      delete next[snapshot.id];
      return next;
    });
  }, []);

  /** True when a snapshot means the position is no longer open. */
  const isClosedSnapshot = useCallback((eventType, normalized) => {
    if (eventType === 'POSITION_CLOSED') return true;
    if (normalized.status === 'closed') return true;
    if (normalized.qty !== undefined && Number(normalized.qty) === 0) return true;
    return false;
  }, []);

  /**
   * Handle one inbound `POSITION_UPDATE` event. `event.data` may be a single
   * snapshot object OR an array (live ticks are coalesced), so normalize first.
   * Each snapshot is a delta — patch it by `positionId` onto whatever we have.
   */
  const onEvent = useCallback(
    (event) => {
      if (!event || typeof event !== 'object') return;

      // Discard stale events (clock skew / out-of-order delivery).
      if (event.timestamp) {
        const ageMs = Date.now() - new Date(event.timestamp).getTime();
        if (!Number.isNaN(ageMs) && ageMs > MAX_EVENT_AGE_MS) return;
      }

      setLastEvent(event);

      const owned = ownedRef.current;
      const snapshots = Array.isArray(event.data) ? event.data : [event.data];

      for (const snapshot of snapshots) {
        const normalized = normalizeSnapshot(snapshot);
        if (!normalized) continue;

        // Fall back to the envelope accountId if the snapshot omits it.
        const acct = normalized.account_id ?? event.accountId;
        if (acct !== undefined) normalized.account_id = acct;

        // Defensive: only apply positions for accounts this client owns (when
        // the owned set is known).
        if (owned.size > 0 && acct !== undefined && !owned.has(acct)) continue;

        if (isClosedSnapshot(event.type, normalized)) applyRemove(normalized);
        else applyUpsert(normalized);
      }
    },
    [applyUpsert, applyRemove, isClosedSnapshot]
  );

  // -----------------------------------------------------------------------
  // Initial REST seed (runs even when the WS layer is disabled/offline).
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!enabled || ids.length === 0 || !accessToken) {
      setPositionsById((prev) => (Object.keys(prev).length ? {} : prev));
      setLoading(false);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all(ids.map((id) => reconcileAccount(id, { silent: true })))
      .then(() => {
        if (!cancelled) setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setLoading(false);
          setError('Unable to load positions.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, idsKey, accessToken, reconcileAccount]);

  // -----------------------------------------------------------------------
  // WebSocket layer: ONE plain `ws` socket subscribing to every owned account.
  // (Not socket.io — the portal relays positions over a raw WebSocket on 8080.)
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!enabled || !wsEnabled || ids.length === 0 || !WS_URL) {
      setStatus('disabled');
      return undefined;
    }
    if (typeof WebSocket === 'undefined') {
      setStatus('disabled');
      return undefined;
    }

    let disposed = false;

    const subscribeAll = (socket) => {
      // The server forgets subscriptions on disconnect, so re-send on every
      // (re)connect. One socket may watch several accounts.
      for (const acctId of ids) {
        socket.send(JSON.stringify({ type: 'SUBSCRIBE', accountId: acctId }));
      }
    };

    const open = () => {
      if (disposed) return;
      let socket;
      try {
        socket = new WebSocket(WS_URL);
      } catch {
        scheduleReconnect();
        return;
      }
      socketRef.current = socket;

      socket.onopen = () => {
        if (disposed) return;
        reconnectAttemptRef.current = 0;
        setStatus('online');
        subscribeAll(socket);
        // Hard-guarantee backstop: heal any cache that went stale while down.
        ids.forEach((acctId) => reconcileAccount(acctId, { silent: true }));
      };

      socket.onmessage = (msg) => {
        if (disposed) return;
        let payload;
        try {
          payload = JSON.parse(msg.data);
        } catch {
          return;
        }
        if (!payload || typeof payload !== 'object') return;
        if (payload.type === 'POSITION_SNAPSHOT') {
          applyAccountSnapshot(payload.accountId, payload.positions);
        } else if (payload.type === 'POSITION_UPDATE') {
          onEvent(payload.event);
        }
      };

      socket.onerror = () => {
        // Errors are always followed by `close`; let close drive reconnect.
        try {
          socket.close();
        } catch {
          /* noop */
        }
      };

      socket.onclose = () => {
        if (disposed) return;
        socketRef.current = null;
        setStatus('connecting');
        scheduleReconnect();
      };
    };

    const scheduleReconnect = () => {
      if (disposed) return;
      // Exponential backoff capped at 5s (a fresh snapshot arrives on reopen).
      const attempt = reconnectAttemptRef.current;
      const delay = Math.min(1000 * 2 ** attempt, 5000);
      reconnectAttemptRef.current = attempt + 1;
      reconnectTimerRef.current = setTimeout(open, delay);
    };

    setStatus('connecting');
    open();

    return () => {
      disposed = true;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      const socket = socketRef.current;
      socketRef.current = null;
      if (socket) {
        socket.onopen = null;
        socket.onmessage = null;
        socket.onerror = null;
        socket.onclose = null;
        try {
          socket.close();
        } catch {
          /* noop */
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, wsEnabled, idsKey, accessToken, reconcileAccount, onEvent, applyAccountSnapshot]);

  // -----------------------------------------------------------------------
  // Optional safety-net REST poll (off by default — WS is the live path).
  // -----------------------------------------------------------------------
  const refresh = useCallback(() => {
    ids.forEach((id) => reconcileAccount(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, reconcileAccount]);

  useEffect(() => {
    if (!enabled || !accessToken || !reconcileIntervalMs || ids.length === 0) {
      return undefined;
    }
    const timer = setInterval(refresh, reconcileIntervalMs);
    return () => clearInterval(timer);
  }, [enabled, accessToken, reconcileIntervalMs, idsKey, refresh]);

  const positions = useMemo(() => Object.values(positionsById), [positionsById]);

  return {
    positions,
    positionsById,
    totalCount: positions.length,
    loading,
    error,
    status,
    connected: status === 'online',
    lastEvent,
    refresh,
  };
};

export default usePositionSync;


